import { useEffect, useState } from 'react';
import { Alert, Button, Col, Descriptions, message, Modal, Row, Select, Space, Typography } from 'antd';
import { useQueryClient } from 'react-query';
import {
  useGetPrice,
  useGetProducts,
  useSubscription,
} from '../../../../../../../../Hooks/TT_ELD/Companies';
import { useChangeSubscriptionPrice } from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { apiErrorMessage } from '../../../../../../../../API/TT_ELD/legalMigration';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';

const { Text } = Typography;

// e.g. "15 USD", "15 USD / month", "With IFTA · 20 USD / month"
const priceLabel = (price: any) => {
  const amount = `${(price.unit_amount ?? 0) / 100} ${price.currency?.toUpperCase() || ''}`.trim();
  const interval = price.recurring?.interval ? ` / ${price.recurring.interval}` : '';
  return price.nickname ? `${price.nickname} · ${amount}${interval}` : `${amount}${interval}`;
};

// Price amount with a copy-to-clipboard icon for its Stripe price ID
const PriceWithCopy = ({ price, priceId }: { price?: any; priceId?: string | null }) => {
  if (!priceId) return <>-</>;
  return (
    <Text copyable={{ text: priceId, tooltips: ['Copy price ID', 'Price ID copied'] }}>
      {price ? priceLabel(price) : priceId}
    </Text>
  );
};

interface Props {
  companyId: string;
  company: any;
  isLegal: boolean;
  importRunning: boolean;
  canManage: boolean;
  // Price changes are allowed only once Step 1 has switched the company to its current ELD type
  migrationStarted: boolean;
}

const SubscriptionStep = ({
  companyId,
  company,
  isLegal,
  importRunning,
  canManage,
  migrationStarted,
}: Props) => {
  const canEdit = canManage && migrationStarted;
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState<string | undefined>();
  const [priceId, setPriceId] = useState<string | undefined>();
  // The backend returns legal or default products/prices based on the company's ELD type
  const { data: products, isLoading: productsLoading } = useGetProducts(companyId);
  const { data: prices, isFetching: pricesLoading } = useGetPrice(companyId, productId);
  const changePrice = useChangeSubscriptionPrice(companyId);
  // The company only stores the price ID; the subscription has the actual amount. Pick the item
  // on the company's plan (a legal subscription also has a processing-fee item).
  const { data: subscriptionInfo } = useSubscription(company?.subscriptionId, companyId);
  const subscriptionItems: any[] = subscriptionInfo?.data?.subscription?.items?.data ?? [];
  const currentPrice = subscriptionItems.find((item) => item?.price?.id === company?.priceId)?.price;

  // A company-type switch changes which plans are allowed, so start the selection over
  useEffect(() => {
    setProductId(undefined);
    setPriceId(undefined);
  }, [isLegal]);

  const productOptions = (products?.products ?? []).map((p: any) => ({ label: p.name, value: p.id }));
  const priceOptions = (prices?.prices ?? []).map((p: any) => ({ label: priceLabel(p), value: p.id }));
  const selectedPrice = (prices?.prices ?? []).find((p: any) => p.id === priceId);

  const hasSubscription = !!company?.subscriptionId;
  const priceRequired = isLegal;
  const canSubmit = canEdit && hasSubscription && (!priceRequired || !!priceId);

  const onSubmit = () => {
    const target = selectedPrice ? priceLabel(selectedPrice) : `the ${DEFAULT_ELD} plan`;
    Modal.confirm({
      title: 'Change the subscription price?',
      okText: 'Change price',
      content: (
        <div>
          <p>
            The Stripe subscription moves from{' '}
            <Text strong>{currentPrice ? priceLabel(currentPrice) : company?.priceId || 'the current plan'}</Text> to{' '}
            <Text strong>{target}</Text>. The quantity ({company?.quantity ?? '-'}) stays the same.
          </p>
          {isLegal && <p>The matching {STANDARD_ELD} processing fee is added.</p>}
          {!isLegal && !priceId && <p>The {STANDARD_ELD} processing fee is removed.</p>}
        </div>
      ),
      onOk: async () => {
        try {
          await changePrice.mutateAsync(priceId);
          message.success('Subscription price changed');
          setPriceId(undefined);
          queryClient.invalidateQueries(`getPrice/${companyId}`);
          queryClient.invalidateQueries(`subscription/${company?.subscriptionId}`);
        } catch (error) {
          message.error(apiErrorMessage(error));
          throw error; // keep the confirm open so the admin sees what happened
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
        <Descriptions.Item label="Current price">
          <PriceWithCopy price={currentPrice} priceId={company?.priceId} />
        </Descriptions.Item>
        <Descriptions.Item label="Quantity">{company?.quantity ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="Stripe status">{company?.stripeStatus || '-'}</Descriptions.Item>
      </Descriptions>

      {!hasSubscription ? (
        <Alert type="info" showIcon message="This company has no subscription, so there is no price to change." />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message={
              isLegal
                ? `${STANDARD_ELD} company: choose a plan with IFTA or without IFTA. The matching processing fee is added.`
                : `${DEFAULT_ELD} company: only a subscription that is still on a ${STANDARD_ELD} plan (with or without IFTA) can be moved. Leave the price empty to move it to the ${DEFAULT_ELD} plan.`
            }
          />
          {!migrationStarted && (
            <Alert
              type="warning"
              showIcon
              message="Read-only until Step 1 is done"
              description={`Start the migration in Step 1 first. Once the company has been switched to ${isLegal ? DEFAULT_ELD : STANDARD_ELD}, the price can be changed here.`}
            />
          )}
          {migrationStarted && importRunning && (
            <Alert
              type="warning"
              showIcon
              message="Drivers and logs are still being migrated in the background. You can change the price now."
            />
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text>Product</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select product"
                allowClear
                loading={productsLoading}
                value={productId}
                onChange={(value) => {
                  setProductId(value);
                  setPriceId(undefined);
                }}
                options={productOptions}
                disabled={!canEdit}
              />
            </Col>
            <Col xs={24} md={12}>
              <Text>Price{priceRequired ? '' : ' (optional)'}</Text>
              <Select
                style={{ width: '100%' }}
                placeholder={priceRequired ? 'Select price' : `${DEFAULT_ELD} plan`}
                allowClear
                loading={pricesLoading}
                value={priceId}
                onChange={setPriceId}
                options={priceOptions}
                disabled={!canEdit || !productId}
              />
            </Col>
          </Row>
          <div>
            <Button type="primary" disabled={!canSubmit} loading={changePrice.isLoading} onClick={onSubmit}>
              {!isLegal && !priceId ? `Move to ${DEFAULT_ELD} plan` : 'Change price'}
            </Button>
          </div>
        </>
      )}
    </Space>
  );
};

export default SubscriptionStep;
